import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LaunchIcon from "@mui/icons-material/Launch";
import Linkify from "linkify-react";

import LabDetails from "../../components/PullListComponents/LabDetails";
import LegacyLabsDetails from "../../components/PullListComponents/LegacyLabDetails";
import Title from "../../components/PullListComponents/Title";
import Download from "../../components/XplanComponents/Download";
import RelatedJiraTicketsSimple from "../../components/PullListComponents/RelatedJiraTicketsSimple";
import PullListFormEdit from "./PullListFormEdit";
import pullListLabels from "../../util/PullListLabels";
import "./PlanStyles.css";

const PullListForm = ({ index, form, updateForm, pullListFolderId, sampleDisposal, lab, labLegacyAddress, couriers, isEPApproved, onCancel }) => {
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

    useEffect(() => {
        if (form.isNewSeries === true) {
            setIsEditOpen(true);
        }
    }, []);

    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    const handleEditClick = () => {
        setIsEditOpen(true);
    };

    const handleEditClose = (e) => {
        window.dispatchEvent(
            new Event("beforeunload", { bubbles: true, cancelable: true })
        );
        setTimeout(() => {
            if (hasUnsavedChangesRef.current) {
                e.preventDefault();
                const userConfirmed = window.confirm(
                    "You have unsaved changes. Do you want to close without saving?"
                );
                if (userConfirmed) {
                    if (form.isNewSeries === true) {
                        onCancel();
                    }
                    setIsEditOpen(false);
                }
                setHasUnsavedChanges(false);
            } else {
                if (form.isNewSeries === true) {
                    onCancel();
                }
                setIsEditOpen(false);
            }
        }, 1);
    };

    return (
        <div className="planSection">
            {form && !form.isNewSeries && (
                <div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Title form={form} />
                        <Button
                            variant="contained"
                            onClick={handleEditClick}
                            className="editButton"
                            startIcon={<EditIcon />}
                        >
                            Edit
                        </Button>
                    </div>

                    <div className="dividerLine" />

                    <div className="pullListForm">
                        <div className="pullListSingleField">
                            <h3 className="pullListFileTitle">
                                {form.data.pullListFileVersion > 0
                                    ? `${pullListLabels.pullListFile}: Version ${form.data.pullListFileVersion}`
                                    : `${pullListLabels.pullListFile}`}
                            </h3>
                            <div>
                                {form.data.pullListFile !== null && (
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <Download
                                            fileId={form.data.pullListFile}
                                            fileName={`EP${form.data.epId}_PL${form.data.pullListId}`}
                                        />
                                        <Button
                                            variant="text"
                                            color="primary"
                                            startIcon={<LaunchIcon/>}
                                            onClick={() => {
                                                window.open(`https://bri.app.box.com/folder/${form.data.boxFilesId}`);
                                            }}
                                            title={`https://bri.app.box.com/folder/${form.data.boxFilesId}`}
                                        >
                                            VIEW OTHER FILES
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="pullListFieldTwoColumnInfo">
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {pullListLabels.shipmentFromRepo}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.shipmentFromRepo}
                                </div>
                            </div>
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {pullListLabels.rushOrder}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.rushOrder ? "Yes" : "No"}
                                </div>
                            </div>
                        </div>

                        <div className="pullListFieldTwoColumnInfo">
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {pullListLabels.status}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.status}
                                </div>
                            </div>
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {pullListLabels.approvalDate}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.approvalDate && form.data.approvalDate.replace(/(.+)-(.+)-(.+)/, "$2-$3-$1")}
                                </div>
                            </div>
                        </div>
                        {sampleDisposal === "No" && (
                            form.data.labName ? (
                                <LabDetails
                                    labName={form.data.labName}
                                    labDesc={form.data.labDesc}
                                    labAddress1={form.data.labAddress1}
                                    labAddress2={form.data.labAddress2}
                                    labAddress3={form.data.labAddress3}
                                    labAddress4={form.data.labAddress4}
                                    labCity={form.data.labCity}
                                    labState={form.data.labState}
                                    labPostalCode={form.data.labPostalCode}
                                    labContact={form.data.labContact}
                                    labPhoneNumber={form.data.labPhoneNumber}
                                    labEmail={form.data.labEmail}
                                    labPI={form.data.labPI}
                                />
                            ) : (
                                lab ? <LabDetails lab={lab}/>
                                    : labLegacyAddress && <LegacyLabsDetails labLegacyAddress={labLegacyAddress}/>
                            )
                        )}
                        <div className="pullListFieldTwoColumnInfo">
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {pullListLabels.numberOfSamples}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.numberOfSamples}
                                </div>
                            </div>
                            {
                                sampleDisposal === "Yes" || form.data.shipmentFromRepo === "No" ?
                                    <div className="pullListField"/>
                                    :
                                    <div className="pullListField">
                                        <h3 className="pullListFieldTitle">
                                            {pullListLabels.shippingTemperature}
                                        </h3>
                                        <div className="pullListFieldCopy">
                                            {form.data.shippingTemperature}
                                        </div>
                                    </div>
                            }
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {sampleDisposal === "No" ? pullListLabels.informedConsent : pullListLabels.disposalRequest} (Approver
                                    1)
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.informedConsent}
                                </div>
                            </div>
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {sampleDisposal === "No" ? pullListLabels.informedConsentApprover : pullListLabels.disposalRequestApprover} (Approver
                                    2)
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.informedConsentApprover}
                                </div>
                            </div>
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {pullListLabels.approvalDate}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.approvalDate && form.data.approvalDate.replace(/(.+)-(.+)-(.+)/, "$2-$3-$1")}
                                </div>
                            </div>
                            <div className="pullListField">
                                <h3 className="pullListFieldTitle">
                                    {sampleDisposal === "No" ? pullListLabels.shipDate : pullListLabels.disposalDate}
                                </h3>
                                <div className="pullListFieldCopy">
                                    {form.data.shipDate && form.data.shipDate.replace(/(.+)-(.+)-(.+)/, "$2-$3-$1")}
                                </div>
                            </div>
                            {sampleDisposal === "No" && (
                                <>
                                    <div className="pullListField">
                                        <h3 className="pullListFieldTitle">
                                            {pullListLabels.resultsReceivedDate}
                                        </h3>
                                        <div className="pullListFieldCopy">
                                            {form.data.resultsReceivedDate && form.data.resultsReceivedDate.replace(/(.+)-(.+)-(.+)/, "$2-$3-$1")}
                                        </div>
                                    </div>
                                    <div className="pullListField">
                                        <h3 className="pullListFieldTitle">
                                            {pullListLabels.resultsValidatedDate}
                                        </h3>
                                        <div className="pullListFieldCopy">
                                            {form.data.resultsValidatedDate && form.data.resultsValidatedDate.replace(/(.+)-(.+)-(.+)/, "$2-$3-$1")}
                                        </div>
                                    </div>
                                    <div className="pullListField">
                                        <h3 className="pullListFieldTitle">
                                            {pullListLabels.validationStatus}
                                        </h3>
                                        <div className="pullListFieldCopy">
                                            {form.data.validationStatus}
                                        </div>
                                    </div>
                                    <div className="pullListField">
                                        <h3 className="pullListFieldTitle">
                                            {pullListLabels.validationBy}
                                        </h3>
                                        <div className="pullListFieldCopy">
                                            {form.data.validationBy}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {sampleDisposal === "No" && (
                            <>
                                <div className="pullListSingleField">
                                    <h3 className="pullListFieldTitle">
                                        Related Jira Tickets
                                    </h3>
                                    <div>
                                        <RelatedJiraTicketsSimple
                                            tickets={form.data.relatedJiraTickets}
                                        />
                                    </div>
                                </div>

                                <div className="pullListSingleField">
                                    <h3 className="pullListFieldTitle">
                                        {pullListLabels.datasetsValidationLink}
                                    </h3>
                                    <div style={{ whiteSpace: "pre-wrap" }} className="pullListFieldCopy">
                                        <Linkify options={{ target: "_blank", rel: "noopener noreferrer" }}>
                                            {form.data.datasetsValidationLink}
                                        </Linkify>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {form && (
                <PullListFormEdit
                    isOpen={isEditOpen}
                    onClose={handleEditClose}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                    index={index}
                    form={form}
                    updateForm={updateForm}
                    pullListFolderId={pullListFolderId}
                    sampleDisposal={sampleDisposal}
                    lab={lab}
                    labLegacyAddress={labLegacyAddress}
                    couriers={couriers}
                    isEPApproved={isEPApproved}
                />
            )}
        </div>
    );
};

export default PullListForm;
